import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardMedia from '@mui/material/CardMedia'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import ImageNotAvailable from '@/assets/img/image-not-available.png'

import colors from '@/assets/scss/_themes-vars.module.scss'

export type DeviceCardProps = {
  /**
   * The device id
   */
  id: string
  /**
   * The source of device's image
   */
  imgUrl: string,
  /**
   * If `true`, the device is available
   */
  isAvailable?: boolean
  /**
   * The device name
   */
  name: string
  /**
   * Callback fired when preview button is clicked
   * @param {string} id device id
   */
  onPreview?: (id: string) => void
  /**
   * Callback fired when delete button is clicked
   * @param {string} id device id
   */
  onDelete?: (id: string) => void
  /**
   * The device type
   */
  type?: string
}

export function DeviceCard(props: DeviceCardProps) {
  const { id, imgUrl, isAvailable = false, name, onPreview, onDelete, type } = props
  const { t } = useTranslation()
  const [imgSrc, setImgSrc] = useState<string>(imgUrl)

  const handleLoadError = () => {
    setImgSrc(ImageNotAvailable)
  }

  return (
    <Card sx={{ p: '24px' }}>
      <Grid container>
        <Grid item xs={6}>
          <Stack justifyContent="space-between" spacing={2}>
            <Box
              sx={{
                display: 'flex',
                width: '100px',
                height: '100px'
              }}
            >
              <CardMedia
                alt="device-image"
                component="img"
                data-testid="device-image"
                onError={handleLoadError}
                src={imgSrc}
                sx={{
                  width: '100%',
                  objectFit: 'contain'
                }}
              />
            </Box>
            <Stack spacing={1}>
              <Typography variant="h6" component="div" sx={{ color: colors.gray900, fontSize: '18px', fontWeight: 500, lineHeight: '28px' }}>
                {name}
              </Typography>
              {type && (
                <Typography variant="subtitle1" component="div" sx={{ color: colors.gray500, fontSize: '14px', lineHeight: '20px' }}>
                  {type}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={6}>
          <Stack alignItems="end" justifyContent="space-between" sx={{ height: '100%' }}>
            <Chip
              color={isAvailable ? 'success' : 'error'}
              label={t(`component.ui.deviceCard.status.${isAvailable ? 'available' : 'unavailable'}`)}
              variant="filled"
              size="small"
            />
            <Stack direction="row" spacing={1}>
              {onPreview && <Button color="primary" disableElevation variant="contained" onClick={() => onPreview?.(id)} size="small">
                {t('component.ui.deviceCard.previewButton.label')}
              </Button>}
              {onDelete && <Button color="error" disableElevation variant="contained" onClick={() => onDelete?.(id)} size="small">
                {t('common:button.delete.label')}
              </Button>}
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Card>
  )
}

export default DeviceCard
